import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';

import { useWindowWidth } from '../../../hooks/useWindowWidth';
import { useUserStore } from '../../../stores/user';
import { PageError } from '../../../types';
import { getBpWidth } from '../../../utils';

import { Header, SideNav, SideNavType } from '../headerSideNav';
import PageContent from './pageContent';

type PageType =
  | 'onboarding' // transparent header; no side nav
  | 'user-dashboard' // header only on mobile; main side nav
  | 'project' // header only on mobile; project side nav
  | 'header-only' // header only
  | 'generic' // logged in – acts as 'user-dashboard'; not logged in – 'header-only'
  | 'full-page'; // no header; no side nav; content is entire page

interface Props {
  type: PageType;
  title?: string;
  description?: string;
  children: React.ReactNode;
  isLoading?: boolean;
  pageError?: PageError;
  className?: string;
  noTips?: boolean;
}

const PageWrapper: React.FC<Props> = (props) => {
  const {
    type,
    title = '',
    description = '',
    children,
    isLoading,
    pageError,
    className = '',
    noTips,
  } = props;

  const { user, accessToken } = useUserStore();

  const windowWidth = useWindowWidth();
  const isMobileDevice = useMemo(
    () => windowWidth < getBpWidth('lg'),
    [windowWidth],
  );

  const [isSideNavOpen, setIsSideNavOpen] = useState(!isMobileDevice);

  let sideNavType: SideNavType =
    type === 'project' ? 'project' : type === 'user-dashboard' ? 'main' : null;
  let hasHeader = ['onboarding', 'header-only'].includes(type);

  if (type === 'generic')
    if (user && accessToken) sideNavType = 'main';
    else hasHeader = true;

  if (!!sideNavType && isMobileDevice) hasHeader = true;

  // if view is changed to mobile – close side nav
  useEffect(() => {
    if (isMobileDevice) setIsSideNavOpen(false);
  }, [isMobileDevice]);

  let contentClass = classNames(
    // base classes
    'flex flex-col box-border overflow-y-scroll transition-all',

    type !== 'onboarding' && 'bg-white',

    // layout classes
    type === 'full-page'
      ? { 'w-full h-full': true }
      : {
          // border radius
          'lg:rounded-t-md': !!sideNavType && !hasHeader,

          // vertical positioning
          'h-[calc(100dvh_-_var(--header-height))]': hasHeader,
          'mt-[var(--page-spacing)] h-[calc(100dvh_-_var(--page-spacing))]':
            !hasHeader,

          // horizontal positioning
          'w-[var(--page-width)]': !!sideNavType && !isMobileDevice,
          'w-screen': !sideNavType || isMobileDevice,
          '-ml-[var(--side-nav-width)]': sideNavType && isMobileDevice,
        },

    // padding classes
    type !== 'full-page' &&
      !className.includes('p-0') && {
        'px-[5vw] pb-[calc(var(--page-spacing)_*_2)] pt-[var(--page-spacing)]': true,
        'lg:px-[calc(var(--page-spacing)_*_2)]': !!sideNavType,
        'lg:px-[calc((100vw_-_var(--page-width))_/_2_+_(var(--page-spacing)_*_2))]':
          !sideNavType,
      },

    className,
  );

  const helmet = (title || description) && (
    <Helmet>
      {title && <title>{title} | CrowdClip</title>}
      {description && <meta name="description" content={description} />}
    </Helmet>
  );

  const content = (
    <main className={contentClass}>
      <PageContent
        children={children}
        isLoading={isLoading}
        pageError={pageError}
        noTips={noTips}
      />
    </main>
  );

  if (type === 'full-page')
    return (
      <React.Fragment>
        {helmet}

        {content}
      </React.Fragment>
    );

  return (
    <React.Fragment>
      {helmet}

      <div
        className={classNames('flex flex-col', {
          'bg-grey-100': type !== 'onboarding',
        })}
      >
        {hasHeader && (
          <Header
            hasSideNav={!!sideNavType}
            toggleSideNav={() => setIsSideNavOpen(!isSideNavOpen)}
            variant={type === 'onboarding' ? 'transparent' : 'grey'}
          />
        )}

        <div className="flex">
          {!!sideNavType && user && (
            <SideNav
              type={sideNavType}
              show={isSideNavOpen || !isMobileDevice}
            />
          )}

          {/* overlay when side nav is open on mobile */}
          <div
            className={classNames({
              'fixed lg:hidden opacity-0 w-screen h-screen bg-black-overlay-600 z-10 transition-all': true,
              'opacity-100': isSideNavOpen,
              'pointer-events-none': !isSideNavOpen,
            })}
            onClick={() => setIsSideNavOpen(false)}
          />

          {content}
        </div>
      </div>
    </React.Fragment>
  );
};

export default PageWrapper;
